import { useCallback } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { isAdminSessionValid, lockAdminSession } from '@/modules/admin/application/adminSession';
import { useIdleTimeout } from '@/shared/hooks/useIdleTimeout';
import { ADMIN_IDLE_TIMEOUT_MS } from '@/shared/config/terminal.config';

/**
 * Guard that wraps all /admin/* child routes.
 *
 * Mounts an invisible idle-timeout watcher (Slice F — admin-idle-timeout spec).
 * After ADMIN_IDLE_TIMEOUT_MS (3 min) of inactivity, the admin session is locked
 * and the user is redirected to the root route.
 *
 * IMPORTANT: useIdleTimeout MUST be called before the isAdminSessionValid() early
 * return to satisfy React's rules of hooks (no hooks after conditional returns).
 * The hook is a no-op in terms of visible output, so calling it when the session
 * is invalid is harmless — the component immediately renders <Navigate />, which
 * triggers navigation away from this subtree, causing RequireAdmin to unmount and
 * the hook's cleanup to run (clearing the timer and all listeners).
 */
export function RequireAdmin() {
  const navigate = useNavigate();

  // Stable callback reference — navigate is stable across renders per React Router guarantees.
  const lockAndRedirect = useCallback(() => {
    lockAdminSession();
    navigate('/', { replace: true });
  }, [navigate]);

  // Hook call BEFORE the early-return guard (required by React hooks rules).
  useIdleTimeout({
    timeoutMs: ADMIN_IDLE_TIMEOUT_MS,
    onTimeout: lockAndRedirect,
  });

  if (!isAdminSessionValid()) {
    return <Navigate to="/id-input/acceso-admin" replace />;
  }
  return <Outlet />;
}
